import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Input } from '../input'

interface UserTokenRevealModalProps {
  /** The raw token secret to reveal, or null when nothing to show. */
  token: string | null
  tokenName: string
  /** Called once the user confirms they've saved the token. */
  onDismissConfirmed: () => void
}

/**
 * One-time reveal of a freshly minted personal access token. Intentionally does
 * NOT close on backdrop click or an X — the raw secret is never retrievable
 * again, so the user must either copy it or tick the acknowledgement before
 * "I've copied the token" is enabled.
 */
export const UserTokenRevealModal: React.FC<UserTokenRevealModalProps> = ({
  token,
  tokenName,
  onDismissConfirmed,
}) => {
  const { t } = useTranslation()
  const [justCopied, setJustCopied] = useState(false)
  const [copiedOnce, setCopiedOnce] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  if (!token) return null

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token)
    setJustCopied(true)
    setCopiedOnce(true)
    setTimeout(() => setJustCopied(false), 2000)
  }

  const handleDone = () => {
    setAcknowledged(false)
    setCopiedOnce(false)
    setJustCopied(false)
    onDismissConfirmed()
  }

  // Dismissal is allowed once the user has copied the secret or explicitly
  // acknowledged saving it — the token cannot be shown again.
  const canDismiss = copiedOnce || acknowledged

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4"
      data-testid="user-token-reveal-modal"
    >
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-surface p-6 shadow-xl">
        <div className="border-b border-border pb-4">
          <h3 className="text-xl font-semibold text-text-primary">
            {t('userTokens.tokenCreated')}
          </h3>
          <p className="mt-1 text-sm text-text-placeholder">
            {t('userTokens.tokenLabel')}{' '}
            <span className="font-medium text-text-secondary">{tokenName}</span>
          </p>
        </div>

        <div className="mt-4 space-y-4">
          <div className="border-warning/40 bg-warning/10 rounded-md border p-3 text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{t('userTokens.storeSecurely')}</span>
          </div>

          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={token}
              className="flex-1 font-mono text-xs"
              onFocus={(e) => e.target.select()}
              data-testid="user-token-value"
            />
            <Button
              variant="ghost"
              onClick={handleCopy}
              className="shrink-0"
              data-testid="user-token-copy"
            >
              {justCopied ? (
                <>
                  <CheckIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('common.copied')}
                </>
              ) : (
                <>
                  <ClipboardIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('common.copy')}
                </>
              )}
            </Button>
          </div>

          <label className="flex items-start gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid="user-token-ack"
            />
            {t('userTokens.savedAcknowledgement')}
          </label>

          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              onClick={handleDone}
              disabled={!canDismiss}
              data-testid="user-token-done"
            >
              {t('userTokens.copiedToken')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
